
import {Component, Prop, PropSync, Ref, Vue, Watch} from 'vue-property-decorator';
import TeacherChatMessage from './TeacherChatMessage.vue';
import {getMobileOrientation, isInMobileApp} from 'src/utils/MobileUtils';
import CatalogItemSmall from '../catalog/components/cards/CatalogItemSmall.vue';
import CatalogCardScoreEvolution from '../catalog/components/cards/CatalogCardScoreEvolution.vue';
import {playerModals} from '../player/models/PlayerModals';
import AudioCaptureButton from 'src/modules/player/components/controls/other/AudioCaptureButton.vue';
import {userService} from 'src/modules/user/UserService';
import { TUTO_CHALLENGE_URL } from '../onboarding/models/TutoModel';

@Component({
  components: {CatalogCardScoreEvolution, CatalogItemSmall, TeacherChatMessage, AudioCaptureButton},
})
export default class TeacherConv extends Vue {
  @PropSync('isConvDisplayed') isDisplayed: boolean;
  @Ref() readonly messageList!: HTMLElement;
  @Prop({required: true}) messages;
  @Prop({required: true}) timeWithNoResponse: number;
  @Prop({default: false}) isInPlayer: boolean;
  @Prop({default: false}) isListening: boolean;

  private text: string = '';
  private isBetaWarningDisplayed: boolean = localStorage.getItem('sawChatbot') !== 'true';
  private isKeyboardOpen: boolean = false;
  private previousStatusBarColor = null;
  private isUserAtBottom: boolean = true;
  private autoScrollEnabled: boolean = true;

  get conversationStarters() {
    return [
      this.$t('teacherConv.conversationStarters.dontKnowWhatToPlay'),
      this.$t('teacherConv.conversationStarters.cantPlayBothHands'),
      this.$t('teacherConv.conversationStarters.playFrequency'),
      this.$t('teacherConv.conversationStarters.plugPiano'),
      this.$t('teacherConv.conversationStarters.positioning'),
      // this.$t('teacherConv.conversationStarters.levelTest'),
      this.$t('teacherConv.conversationStarters.cantFindLessons'),
      this.$t('teacherConv.conversationStarters.cantHearSound'),
      this.$t('teacherConv.conversationStarters.lowerTempo'),
      this.$t('teacherConv.conversationStarters.showNoteNames'),
      this.$t('teacherConv.conversationStarters.whatSkillsToWork'),
      this.$t('teacherConv.conversationStarters.helpChooseSkill'),
    ]
      .sort(() => 0.5 - Math.random())
      .slice(0, 2);
  }

  get isLandscapeOnMobile() {
    return isInMobileApp() && (getMobileOrientation() === 'landscape' || this.$q.screen.width >= this.$q.screen.height);
  }

  // Matches the parent's maximized-dialog condition: the conv is a bottom sheet
  // (drag handle) on mobile, and a docked card (close button) on desktop.
  get isMobileSheet() {
    return this.$q.screen.lt.sm || this.$q.platform.is.mobile;
  }

  get hasTeacherMessagesQuotaExceeded() {
    return !userService.user.premium && userService.user.configurations.currentWeeklyFreeTeacherMessages <= 0;
  }

  get userService() {
    return userService;
  }

  get userService() {
    return userService;
  }

  get isInputDisabled() {
    return (
      ((this.messages[this.messages.length - 1].loading ||
        this.messages[this.messages.length - 1].fromUser ||
        this.messages[this.messages.length - 1].generating) &&
        this.timeWithNoResponse !== -1) ||
      this.isBetaWarningDisplayed ||
      (!userService.user.premium && userService.user.configurations.currentWeeklyFreeTeacherMessages <= 0)
    );
  }

  getRouteTitle(path) {
    const route = this.$router.options.routes.find((route) => route.path === '/' + path).children[0];
    if (route && route.meta && route.meta.title) {
      return route.meta.title;
    } else {
      return path;
    }
  }

  getInputPlaceholder() {
    if (this.hasTeacherMessagesQuotaExceeded) {
      return this.$t('teacherConv.inputPlaceholderQuotaExceeded');
    }
    if (this.isInputDisabled) {
      return this.$t('teacherConv.inputPlaceholderDisabled');
    }
    return this.$t('teacherConv.inputPlaceholder');
  }

  sendText() {
    if (this.hasTeacherMessagesQuotaExceeded) {
      this.$q.notify({
        timeout: 4000,
        message: this.$t('teacherConv.quotaExceededMessage'),
        position: 'bottom',
        actions: [
          {
            label: this.$t('teacherConv.subscribeButton'),
            color: 'accent',
            handler: () => {
              this.$router.push('/subscribe');
            },
          },
        ],
      });

      this.closeConv();
      return;
    }

    this.$emit('sendText', this.text);
    this.text = '';
  }

  selectStarter(starter) {
    this.text = starter;
    this.sendText();
  }

  closeConv() {
    this.isDisplayed = false;
    this.$nextTick(() => {
      if (this.previousStatusBarColor) {
        this.setStatusBarColor();
      }
    });
  }

  confirmBetaWarning() {
    localStorage.setItem('sawChatbot', 'true');
    this.isBetaWarningDisplayed = false;
  }

  openLink() {
    window.open('https://survey.qwary.com/form/S_wSzSPnasH9Wc_FT15X0J1BuEcPl5gIya8Ck-6hqjM=', '_blank');
  }

  showPlayerParameter(parameter: string) {
    if (!this.isInPlayer) {
      this.$router.push(TUTO_CHALLENGE_URL);
    }
    this.$emit('showPlayerParameter', parameter);
    this.isDisplayed = false;
  }

  navigateToSkill(skill: any) {
    // Fermer le chat
    this.isDisplayed = false;

    // Naviguer vers la compétence spécifique
    if (skill.route) {
      this.$router.push(skill.route);
    } else if (skill.id && skill.skill_category_id) {
      // Route pour les compétences avec catégorie
      this.$router.push(`/school/skill-categories/${skill.skill_category_id}/skills/${skill.id}`);
    } else if (skill.id) {
      // Route alternative pour les compétences sans catégorie spécifiée
      this.$router.push(`/school/skill/${skill.id}`);
    } else {
      // Fallback vers la page des compétences générales
      this.$router.push('/school');
    }
  }

  navigateToSkillCategory(category: any) {
    // Fermer le chat
    this.isDisplayed = false;

    // Naviguer vers la catégorie de compétences
    if (category.route) {
      this.$router.push(category.route);
    } else if (category.id) {
      // Route par défaut basée sur l'ID de catégorie
      this.$router.push(`/school/skill-categories/${category.id}`);
    } else {
      // Fallback vers la page des compétences générales
      this.$router.push('/school');
    }
  }

  @Watch('isDisplayed')
  async setStatusBarColor() {
    if (isInMobileApp() && this.$q.platform.is.capacitor && !this.isInPlayer) {
      if (!this.previousStatusBarColor) {
        // @ts-ignore
        this.previousStatusBarColor = await this.MobileScreen.getStatusBarColor();
        // @ts-ignore
        this.MobileScreen.setStatusBarStyle('white');
        // @ts-ignore
        this.MobileScreen.setNavigationBarColor('grey');
      } else {
        // @ts-ignore
        this.MobileScreen.setStatusBarStyle(this.previousStatusBarColor === '#FFFFFF' ? 'white' : 'primary');
        // @ts-ignore
        this.MobileScreen.setNavigationBarColor('white');
      }
    }
  }

  mounted() {
    this.setStatusBarColor();
    this.setupScrollListener();
  }

  beforeDestroy() {
    this.removeScrollListener();
  }

  setupScrollListener() {
    if (this.messageList) {
      this.messageList.addEventListener('scroll', this.handleScroll);
    }
  }

  removeScrollListener() {
    if (this.messageList) {
      this.messageList.removeEventListener('scroll', this.handleScroll);
    }
  }

  handleScroll() {
    const element = this.messageList;
    const threshold = 10;
    this.isUserAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - threshold;
    this.autoScrollEnabled = this.isUserAtBottom;
  }

  scrollToBottom() {
    this.$nextTick(() => {
      if (this.messageList && this.autoScrollEnabled) {
        this.messageList.scrollTop = this.messageList.scrollHeight;
      }
    });
  }

  @Watch('messages', {deep: true})
  onMessagesChange() {
    this.scrollToBottom();
  }

  containsMarkdown(text: string): boolean {
    if (!text) {
      return false;
    }
    return /(#{1,6}\s|\*\*|__|`|\[.*]\(.*\)|\n\s*[-*+]\s)/.test(text);
  }

  parseMarkdown(text: string): string {
    if (!text) {
      return '';
    }
    let html = this.escapeHtml(text);

    html = html
      .replace(/#{6}\s+(.+?)(?:\n|$)/g, '<div class="text-caption text-weight-medium">$1</div>')
      .replace(/#{5}\s+(.+?)(?:\n|$)/g, '<div class="text-body2 text-weight-medium">$1</div>')
      .replace(/#{4}\s+(.+?)(?:\n|$)/g, '<div class="text-body1 text-weight-medium">$1</div>')
      .replace(/#{3}\s+(.+?)(?:\n|$)/g, '<div class="text-subtitle2 text-weight-medium">$1</div>')
      .replace(/#{2}\s+(.+?)(?:\n|$)/g, '<div class="text-subtitle1 text-weight-medium">$1</div>')
      .replace(/#{1}\s+(.+?)(?:\n|$)/g, '<div class="text-h6 text-weight-medium">$1</div>');

    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
    html = html.replace(/_(.+?)_/g, '<em>$1</em>');

    html = html.replace(/`(.+?)`/g, '<code class="bg-grey-2 q-px-xs" style="border-radius: 3px;">$1</code>');

    html = html.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" class="text-primary">$1</a>');

    html = html.replace(/^\s*[-*+]\s+(.+?)(?:\n|$)/gm, (match, content, offset, string) => {
      const beforeMatch = string.substring(0, offset);
      const lastNewline = beforeMatch.lastIndexOf('\n');
      const lineStart = lastNewline === -1 ? 0 : lastNewline + 1;
      const indentation = match.substring(0, match.indexOf(match.trim()));
      const indentLevel = Math.floor(indentation.length / 2);

      if (indentLevel === 0) {
        return `<div class="q-mb-xs">- ${content}</div>`;
      } else {
        return `<div class="q-ml-md q-mb-xs">• ${content}</div>`;
      }
    });

    html = html.replace(/\n/g, '<br>');

    return html;
  }

  escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
